import React, { useState, useEffect } from 'react';
import { Shield, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getGkSessions } from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';

const thStyle = { padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap', background: 'var(--bg-app)' };
const tdStyle = { padding: '10px 12px', textAlign: 'right', fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap' };
const selectStyle = { padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', cursor: 'pointer' };

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// e.g. "Monday 2-7-2026 MD-2" instead of the raw "2026-07-02 — MD-2".
function formatSessionLabel(dateStr, title) {
  if (!dateStr) return title || 'Session';
  const [y, m, d] = dateStr.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${weekday} ${d}-${m}-${y}${title ? ' ' + title : ''}`;
}

const METRICS = [
  { key: 'gk_load',      label: 'GK Load' },
  { key: 'avg_gk_power', label: 'Avg GK Power' },
  { key: 'dives',        label: 'Dives' },
  { key: 'dives_left',   label: 'Dives Left' },
  { key: 'dives_right',  label: 'Dives Right' },
  { key: 'td',           label: 'TD (m)' },
  { key: 'rd',           label: 'RD >14,4km/u' },
  { key: 'hid',          label: 'HID >19,8km/u' },
  { key: 'acc',          label: 'Acc' },
  { key: 'dec',          label: 'Dec' },
];

const COLUMNS = [
  { key: 'player_name',              label: 'Player',                    align: 'left' },
  { key: 'dives',                    label: 'Dives' },
  { key: 'dives_left',               label: 'Dives L' },
  { key: 'dives_right',              label: 'Dives R' },
  { key: 'gk_load',                  label: 'GK Load' },
  { key: 'avg_gk_power',             label: 'Avg GK Power' },
  { key: 'avg_time_since_last_dive', label: 'Avg Time Since Last Dive' },
  { key: 'td',                       label: 'TD (m)',            format: v => v != null ? Math.round(v).toLocaleString() : '—' },
  { key: 'rd',                       label: 'RD >14,4km/u',      format: v => v != null ? Math.round(v).toLocaleString() : '—' },
  { key: 'hid',                      label: 'HID >19,8km/u',     format: v => v != null ? Math.round(v).toLocaleString() : '—' },
  { key: 'acc',                      label: 'Acc' },
  { key: 'dec',                      label: 'Dec' },
];

function groupSessions(rows) {
  const byKey = {};
  rows.forEach(r => {
    const date = r.session_date ? r.session_date.slice(0, 10) : '';
    const key = `${date}__${r.session_title}`;
    if (!byKey[key]) byKey[key] = { key, date, title: r.session_title, players: [] };
    byKey[key].players.push(r);
  });
  return Object.values(byKey).sort((a, b) => new Date(b.date) - new Date(a.date));
}

// playerOverride (2026-08-20) scopes the whole report to one goalkeeper — used by the player
// mobile app's own GPS & Load tab. The team-wide session-picker/table/chart above all operate
// on `rows`, so pre-filtering rows to just this player's is enough to turn "every GK who
// played this session" into "did I play, and what were my numbers", and "average across GKs
// per session" into "my own value per session" — no separate per-player branches needed.
export default function GoalkeeperReport({ playerOverride } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useUrlParam('session_key', '');
  const [metricKey, setMetricKey] = useState('gk_load');
  const [sortKey, setSortKey] = useState('player_name');
  const [sortDir, setSortDir] = useState('asc');

  const columns = playerOverride ? COLUMNS.filter(c => c.key !== 'player_name') : COLUMNS;

  const load = () => getGkSessions()
    .then(data => setRows(playerOverride ? (data || []).filter(r => String(r.player_id) === String(playerOverride.id)) : (data || [])))
    .catch(() => setRows([]));

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, []);

  const sessionList = groupSessions(rows);

  useEffect(() => {
    if (!sessionList.length) { setSelectedKey(''); return; }
    if (!sessionList.find(s => s.key === selectedKey)) setSelectedKey(sessionList[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const selected = sessionList.find(s => s.key === selectedKey);

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedPlayers = selected ? [...selected.players].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'player_name') {
      return String(a.player_name || a.player_name_raw || '').localeCompare(String(b.player_name || b.player_name_raw || '')) * dir;
    }
    const na = Number(a[sortKey]);
    const nb = Number(b[sortKey]);
    if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
    return String(a[sortKey] || '').localeCompare(String(b[sortKey] || '')) * dir;
  }) : [];

  const last7 = [...sessionList].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-7);
  const chartData = last7.map(s => {
    const vals = s.players.map(p => Number(p[metricKey])).filter(v => !isNaN(v));
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    return {
      label: s.date ? `${s.date.slice(8, 10)}/${s.date.slice(5, 7)}` : '',
      title: s.title,
      avg: Math.round(avg * 10) / 10,
    };
  });
  const metricLabel = METRICS.find(m => m.key === metricKey)?.label || '';

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '30vh', color: 'var(--text-muted)', gap: 12 }}>
      <Shield size={24} strokeWidth={1.5} /><span style={{ fontSize: 15 }}>Loading GK sessions…</span>
    </div>
  );

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Goalkeeper</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
          {playerOverride ? 'Your GK session data.' : 'GK session report. Import CSVs from the Upload zone.'}
        </p>
      </div>

      {!sessionList.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '30vh', gap: 16, opacity: 0.5 }}>
          <Shield size={64} strokeWidth={1} />
          <h2 style={{ fontSize: 20, fontWeight: 600 }}>No GK sessions yet</h2>
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            {playerOverride ? 'No GK sessions have been logged for you yet.' : 'Import a GK session CSV from the Upload zone to get started.'}
          </p>
        </div>
      ) : (
        <>
          {/* ── Session detail ── */}
          <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Session Report</span>
              <select value={selectedKey} onChange={e => setSelectedKey(e.target.value)} style={selectStyle}>
                {sessionList.map(s => (
                  <option key={s.key} value={s.key}>{formatSessionLabel(s.date, s.title)}</option>
                ))}
              </select>
            </div>
            {selected && (playerOverride ? (
              // Player-scoped view: one row worth of metrics, as a tile grid instead of a
              // table — a wide 11-column table read as cramped on a phone-width screen.
              // 4 columns wraps 11 tiles into rows of 4/4/3, per the requested layout.
              <div style={{ padding: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  {columns.map(col => (
                    <div key={col.key} style={{
                      background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 12,
                      padding: '10px 6px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', lineHeight: 1.25, marginBottom: 6, minHeight: 22 }}>
                        {col.label}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                        {col.format ? col.format(sortedPlayers[0]?.[col.key]) : (sortedPlayers[0]?.[col.key] ?? '—')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {columns.map(col => (
                        <th key={col.key} style={{ ...thStyle, textAlign: col.align || 'right', cursor: 'pointer' }} onClick={() => handleSort(col.key)}>
                          {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayers.map((p, i) => (
                      <tr key={p.id} style={{ background: i % 2 !== 0 ? 'var(--bg-app)' : 'transparent' }}>
                        {columns.map(col => (
                          <td key={col.key} style={col.align === 'left' ? { ...tdStyle, textAlign: 'left', fontWeight: 600 } : tdStyle}>
                            {col.key === 'player_name'
                              ? (p.player_name || p.player_name_raw)
                              : col.format ? col.format(p[col.key]) : (p[col.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* ── Trend: last 7 sessions ── */}
          <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', padding: '20px 20px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} /> Last 7 sessions — {playerOverride ? metricLabel : `average ${metricLabel}`}
              </span>
              <select value={metricKey} onChange={e => setMetricKey(e.target.value)} style={selectStyle}>
                {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-strong)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    contentStyle={{ background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                    formatter={(v) => [v, metricLabel]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.title || ''}
                  />
                  <Bar dataKey="avg" fill="#3b82f6" maxBarSize={52} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
